<template>
	<v-form ref="form" @submit.prevent="submit" class="px-2">
		<v-row class="mt-0 mb-5">
			<!-- input data -->
			<v-col lg="9" md="8" cols="12" order-md="0" order="1" class="pb-md-0">
				<v-row class="mt-0 mb-2">
					<!-- title -->
					<v-col lg="6" cols="12">
						<v-text-field
							v-model="title"
							:label="$t('inputs.title')"
							hide-details
							outlined
							dense
							class="rounded-medium"
							:loading="loading"
							:disabled="loading || submitLoading"
							:rules="rules.required"
						></v-text-field>
					</v-col>

					<!-- summary -->
					<v-col lg="6" cols="12">
						<v-text-field
							v-model="summary"
							:label="$t('inputs.summary')"
							hide-details
							outlined
							dense
							class="rounded-medium"
							:loading="loading"
							:disabled="loading || submitLoading"
						></v-text-field>
					</v-col>

					<!-- date-of-publication & is-featured -->
					<v-col lg="6" cols="12" class="d-flex flex-sm-row flex-column align-sm-center">
						<!-- date -->
						<v-menu
							ref="menu"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="date"
									:label="$t('inputs.date')"
									hide-details
									outlined
									dense
									class="rounded-medium"
									prepend-inner-icon="mdi-calendar"
									clearable
									:loading="loading"
									:disabled="loading || submitLoading"
									readonly
									v-bind="attrs"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								v-model="date"
								:max="(new Date().getFullYear() + 1) + new Date().toISOString().slice(4, 10)"
								min="1950-01-01"
								@change="$refs.menu.save(date)"
							></v-date-picker>
						</v-menu>

						<!-- is-featured -->
						<v-checkbox
							v-model="isFeatured"
							:label="$t('inputs.is-featured')"
							hide-details
							outlined
							class="mt-sm-0 ms-4 pt-0"
						></v-checkbox>
					</v-col>
				</v-row>
				
				<!-- SEO fields -->
				<template v-if="showSEO">
					<div class="text-body-1 font-weight-bold mb-3">
						{{$t('SEO-fields')}}
					</div>
					<v-row>
						<!-- SEO keywords -->
						<v-col cols="12">
							<v-text-field
								v-model="SEOkeywords"
								:label="$t('inputs.seo-keywords')"
								hide-details="auto"
								outlined
								dense
								class="rounded-medium"
								:loading="loading"
								:disabled="loading || submitLoading"
								hint="keywords should separated by (,) eg: keyword1, keyword2"
								@input="SEOkeywords = SEOkeywords.replace(/(\w) /g, '$1, ')"
							></v-text-field>
						</v-col>

						<!-- SEO description -->
						<v-col cols="12">
							<v-textarea
								v-model="SEOdescription"
								:label="$t('inputs.seo-description')"
								outlined
								dense
								auto-grow
								rows="1"
								no-resize
								counter
								:hint="SEOdescription && SEOdescription.length > 155 ? 'It\'s best to keep descriptions between 155-160 characters' : null"
								persistent-hint
								:color="SEOdescription && SEOdescription.length > 155 ? 'warning' : null"
								class="rounded-medium"
								:loading="loading"
								:disabled="loading || submitLoading"
								@keypress.enter.prevent
								@change="SEOdescription = SEOdescription.replace(/\n/g, ' ')"
							></v-textarea>
						</v-col>
					</v-row>
				</template>

				<!-- description -->
				<div class="text-body-1 font-weight-bold text-capitalize mb-3">{{$t('inputs.description')}}</div>
				<text-editor v-model="description"/>
			</v-col>

			<v-col cols="auto" class="d-md-block d-none px-1">
				<v-divider vertical/>
			</v-col>

			<!-- drop image -->
			<v-col md cols="12" class="pb-md-0">
				<div class="sticky">
					<div class="text-body-1 font-weight-bold mb-3">
						{{$t('upload-images')}}
					</div>
					<bee-drop-img
						v-model="mainImage"
						dashed
						border-width="2"
						:border-color="isSubmitted && !mainImage ? '#f00' : '#ccc'"
						radius="11"
						height="250px"
						blurable
						clearable
						:loading="loading"
						:disabled="loading || submitLoading"
						:hide-image-name="false"
					>
						<template v-slot:drag-text>
							<span class="text-body-1 font-weight-medium">
								{{$t('drag-text')}}
							</span>
							<span class="text-caption mb-0">
								{{$t('supported-text')}}
							</span>
						</template>
					</bee-drop-img>
					
					<bee-drop-zone
						v-model="images"
						dashed
						drop-zone-classes="pa-3"
						radius="11"
						border-width="2"
						max-height="250"
						:drop-image-props="{
							width: 70,
							height: 70
						}"
						:clear-icon="$route.params.id ? 'mdi-delete' : 'mdi-close'"
						:clear-color="$route.params.id ? 'red' : null"
						:gap-size="4"
						:drop-menu-width="$vuetify.breakpoint.mdAndUp ? 400 : '100%'"
						:drop-menu-max-width="$vuetify.breakpoint.mdAndUp ? 400 : '100%'"
						:drop-menu-left="null"
						show-add-btn
						show-drop-menu
						no-drop-replace
						class="mt-3"
						:loading="loading"
						:disabled="loading || submitLoading"
						@remove-image="removeImage"
					>
						<template v-slot:drag-text>
							<p class="mb-0">
								<span class="d-block text-body-1 font-weight-bold">
									{{$t('upload-more-image')}}
								</span>
								<span class="d-block text-caption mb-0">{{$t('drag-text')}}</span>
							</p>
						</template>
					</bee-drop-zone>
				</div>
			</v-col>
		</v-row>
		<v-btn
			type="submit"
			color="primary"
			class="rounded-medium"
			:loading="submitLoading"
			:disabled="loading || submitLoading"
			@click="isSubmitted = true"
		>
			{{$t('save')}}
		</v-btn>
	</v-form>
</template>

<script>
import moment from 'moment';
import rules from '@/helpers/validation rules'
import { news, SEO } from '@/configs/routes.config';
export default {
	name: 'Product',
	
	data: () => ({
		// helpers
		loading: false,
		submitLoading: false,
		showSEO: SEO.show,
		isSubmitted: false,
		rules,
		
		// data
		title: null,
		summary: null,
		date: null,
		isFeatured: null,
		description: null,
		mainImage: null,
		images: [],
		SEOkeywords: null,
		SEOdescription: null,
	}),

	methods: {
		submit() {
			if (this.$refs.form.validate() && this.mainImage) {
				this.submitLoading = true;
				const actionName = this.$route.params.id ? 'update' : 'create';
				// more images
				const images = this.images.filter(c => typeof c !== 'string');

				this.$store.dispatch(`news/${actionName}`, {
					id: this.$route.params.id,
					title: this.title,
					summary: this.summary,
					date: this.date,
					isFeatured: this.isFeatured,
					content: this.description,
					image: typeof this.mainImage !== 'string' ? this.mainImage : null,
					keywordsSeo: this.SEOkeywords,
					descriptionSeo: this.SEOdescription,
				}).then((data) => {
					const message =
						actionName === 'update'
							? this.$t('$-is-updated-successfully', { name: this.$t('News.news') })
							: this.$t('$-is-created-successfully', { name: this.$t('News.news') });
					this.$eventBus.$emit('show-snackbar', false, message);

					// upload the other images
					if (images.length) {
						this.$eventBus.$emit('show-snackbar', false, this.$t('please-wait-uploading-the-images'))
						this.$store.dispatch('news/uploadImages', {
							id: data.id,
							images,
						}).then(() => {
							this.$router.push({ name: news.name })
						}).finally(() => {
							this.submitLoading = false;
						})
					} else {
						this.$router.push({ name: news.name })
					}
				}).finally(() => {
					if (!images.length) {
						this.submitLoading = false;
					}
				})
			}
		},

		// FIXME show dialog to confirm remove
		removeImage(image) {
			if (this.$route.params.id) {
				const imageId = this.productImages.find(c => c.url === image)?.id;
				if (imageId) {
					this.$store.dispatch('news/deleteImage', {
						id: this.$route.params.id,
						imageId: imageId
					})
				}
			}
		}
	},

	created() {
		if (this.$route.params.id) {
			this.loading = true;
			this.$store.dispatch('news/fetchBySlug', {
				slug: this.$route.params.slug
			}).then((res) => {
				const data = res;
				this.title = data.title;
				this.summary = data.summary;
				this.date = data.date ? moment(data.date).format('YYYY-MM-DD') : null;
				this.isFeatured = data.isFeatured;
				this.description = data.content;
				this.mainImage = data.primaryImage.url;
				this.images = data.images.map(c => c.url).filter(c => c !== data.primaryImage.url);
				this.SEOkeywords = data.keywordsSeo ? data.keywordsSeo : '';
				this.SEOdescription = data.descriptionSeo ? data.descriptionSeo : '';
			}).finally(() => {
				this.loading = false;
			})
		}
	}
};
</script>

<style lang="scss">
</style>